import React, { useMemo } from 'react';
import PolicyApplicationQuestionGroup from './PolicyApplicationQuestionGroup';
import {
  evaluateConditionalBehavior,
  isQuestionGroupVisible,
} from '@shared/policy-application/conditionalLogicUtils';
import { FormParameterValues, interpolateText } from '@client/hooks/useFormParameters';

interface FormSectionProps {
  subsection: PolicyFormSubSection;
  formData: PolicyFormSubmissionData;
  questionRegistry: Record<string, PolicyFormQuestion>;
  onSubmissionChange: (questionRef: string, value: PolicyFormSubmissionDataValue) => void;
  parameters?: FormParameterValues;
}

const PolicyApplicationFormSection: React.FC<FormSectionProps> = ({
  subsection,
  formData,
  questionRegistry,
  onSubmissionChange,
  parameters = {},
}) => {
  // Check if the subsection itself should be visible
  const subsectionEvaluation = useMemo(
    () => evaluateConditionalBehavior(subsection.conditional, formData, parameters),
    [subsection.conditional, formData, parameters],
  );

  // Get only the visible question groups
  const visibleQuestionGroups = useMemo(
    () =>
      subsection.questionGroups.filter(group =>
        isQuestionGroupVisible(group, questionRegistry, formData, parameters),
      ),
    [subsection.questionGroups, questionRegistry, formData, parameters],
  );

  const interpolatedTitle = useMemo(
    () => interpolateText(subsection.title, parameters),
    [subsection.title, parameters],
  );

  // Don't render if the subsection is not visible or has no visible groups
  if (!subsectionEvaluation.visible || visibleQuestionGroups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-xl font-semibold text-gray-900">{interpolatedTitle}</h2>
      </div>

      {visibleQuestionGroups.map(group => (
        <PolicyApplicationQuestionGroup
          key={group.id}
          group={group}
          formData={formData}
          onSubmissionChange={onSubmissionChange}
          questionRegistry={questionRegistry}
          parameters={parameters}
        />
      ))}
    </div>
  );
};

export default PolicyApplicationFormSection;
